import type { ReactNode } from 'react';
import type { BlockedState } from '@/contexts/BannedUserContext';

// gloss
type StatusVariant = Exclude<BlockedState, null>;

type Tone = {
  bg: string;
  fg: string;
  icon: ReactNode;
  title: string;
  defaultMessage: string;
};

const stroke = { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2 } as const;

const Icon = ({ d, className }: { d: string; className: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path {...stroke} d={d} />
  </svg>
);

const TONE_BY_VARIANT: Record<StatusVariant, Tone> = {
  banned: {
    bg: 'bg-red-100',
    fg: 'text-red-600',
    icon: (
      <Icon
        className="h-8 w-8 text-red-600"
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
      />
    ),
    title: 'Account Suspended',
    defaultMessage:
      'This account has been banned and can no longer access this program. If you believe this is a mistake, please contact support.',
  },
  unavailable: {
    bg: 'bg-amber-100',
    fg: 'text-amber-600',
    icon: <Icon className="h-8 w-8 text-amber-600" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    title: 'Signup Unavailable',
    defaultMessage: 'Signup is temporarily unavailable. Please try again later or contact support.',
  },
  pending: {
    bg: 'bg-blue-100',
    fg: 'text-blue-600',
    icon: <Icon className="h-8 w-8 text-blue-600" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    title: 'Pending Review',
    defaultMessage: 'Your application is pending review. You will be notified once a decision has been made.',
  },
  rejected: {
    bg: 'bg-red-100',
    fg: 'text-red-600',
    icon: <Icon className="h-8 w-8 text-red-600" d="M6 18L18 6M6 6l12 12" />,
    title: 'Application Declined',
    defaultMessage: 'Your application was not approved. Please contact support if you believe this is in error.',
  },
  applicationRequired: {
    bg: 'bg-blue-100',
    fg: 'text-blue-600',
    icon: (
      <Icon className="h-8 w-8 text-blue-600" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    ),
    title: 'Application Required',
    defaultMessage: 'This hub requires an approved application. Please apply to request access.',
  },
};

// gloss
type SignupStatusScreenProps = {
  variant: StatusVariant;
  message?: string | null;
  supportEmail?: string | null;
};

// gloss
export const SignupStatusScreen = ({ variant, message, supportEmail }: SignupStatusScreenProps) => {
  const tone = TONE_BY_VARIANT[variant];
  const showAppeal = variant === 'banned' && !!supportEmail;
  const handleClose = () => window.location.reload();
  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-white">
      <button
        aria-label="Close"
        className="absolute right-4 top-4 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        onClick={handleClose}
        type="button"
      >
        <Icon className="h-6 w-6" d="M6 18L18 6M6 6l12 12" />
      </button>
      <div className="w-full max-w-md px-6 text-center">
        <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full ${tone.bg}`}>
          {tone.icon}
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">{tone.title}</h1>
        {}
        {(!showAppeal || message) && (
          <p className="mt-3 text-gray-600">{showAppeal ? message : message || tone.defaultMessage}</p>
        )}
        {showAppeal && (
          <p className="mt-3 text-gray-600">
            If you believe this was done in error and would like to submit an appeal, please reach out to us at{' '}
            <a className="font-medium text-blue-600 underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
            .
          </p>
        )}
      </div>
    </div>
  );
};
